import { type PropsWithChildren, useEffect, useRef, useState } from "react";
import type { SessionResponseDto } from "@madamuh/domain";
import { useLocation } from "react-router-dom";
import { GENERIC_TENANT_LABELS, getTenantLabels } from "../../lib/tenantLabels";
import { HamburgerIcon } from "../icons/AppIcons";
import { ProtectedRoleDrawer } from "./ProtectedRoleDrawer";
import { CircularAvatarEditorDialog } from "../profile/CircularAvatarEditorDialog";
import { useViewerProfilePhoto } from "../profile/useViewerProfilePhoto";
import type { OperatorBottomNavigationItem } from "./OperatorBottomNavigation";
import { OperatorBottomNavigation } from "./OperatorBottomNavigation";
import { type ProtectedAppArea, useProtectedRoleDrawer } from "./useProtectedRoleDrawer";

type OperatorAppShellProps = PropsWithChildren<{
  tenantSlug: string;
  tenantName: string | null | undefined;
  session: SessionResponseDto | null;
  currentArea: Exclude<ProtectedAppArea, "family">;
  pageTitle: string;
  pageDescription?: string;
  bottomItems: OperatorBottomNavigationItem[];
  layoutVariant?: "default" | "wide";
}>;

export function OperatorAppShell({
  tenantSlug,
  tenantName,
  session,
  currentArea,
  pageTitle,
  pageDescription,
  bottomItems,
  layoutVariant = "default",
  children
}: OperatorAppShellProps) {
  const location = useLocation();
  const closeButtonRef = useRef<HTMLButtonElement | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [photoEditorOpen, setPhotoEditorOpen] = useState(false);
  const { actionItems, errorMessage, viewerName, viewerRoleLabel } = useProtectedRoleDrawer({
    tenantSlug,
    session,
    currentArea
  });
  const labels = session ? getTenantLabels(session.tenant.uiLabelProfile) : GENERIC_TENANT_LABELS;
  const viewerPhoto = useViewerProfilePhoto(tenantSlug, "operator", Boolean(session));
  const contractOnly = session?.commercialAccess.accessMode === "contractOnly";

  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!drawerOpen) {
      return;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    closeButtonRef.current?.focus();

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setDrawerOpen(false);
      }
    };

    window.addEventListener("keydown", handleEscape);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleEscape);
    };
  }, [drawerOpen]);

  return (
    <main className="family-app-shell">
      <div className={`family-app-shell__viewport${layoutVariant === "wide" ? " family-app-shell__viewport--wide" : ""}`}>
        <header className="family-app-shell__header">
          <div className="family-app-shell__brand">
            <h1 className="family-app-shell__brand-name">{tenantName || "MADAMUH LESSON"}</h1>
          </div>
          {!contractOnly ? <button
              type="button"
              className="family-app-shell__menu-button"
              aria-label="メニューを開く"
              aria-expanded={drawerOpen}
              onClick={() => setDrawerOpen(true)}
            >
              <HamburgerIcon size={28} />
            </button> : null}
        </header>

        <section
          className={[
            "family-app-shell__content",
            bottomItems.length > 0 ? "family-app-shell__content--with-nav" : ""
          ].filter(Boolean).join(" ")}
        >
          <section className="family-surface family-section operator-page-heading">
            <h2 className="family-page-title">{pageTitle}</h2>
            {pageDescription ? <p className="family-page-intro">{pageDescription}</p> : null}
          </section>
          {children}
        </section>
      </div>

      {!contractOnly ? <>
        <OperatorBottomNavigation items={bottomItems} ariaLabel={`${labels.teacher}・管理者向けページナビゲーション`} />
        <ProtectedRoleDrawer
          open={drawerOpen}
          tenantSlug={tenantSlug}
          actionItems={actionItems}
          closeButtonRef={closeButtonRef}
          errorMessage={errorMessage}
          viewerName={viewerName}
          viewerRoleLabel={viewerRoleLabel}
          viewerPhotoUrl={viewerPhoto.photo?.downloadUrl ?? null}
          onEditViewerPhoto={() => { setDrawerOpen(false); setPhotoEditorOpen(true); }}
          onClose={() => setDrawerOpen(false)}
        />
        <CircularAvatarEditorDialog open={photoEditorOpen} onCancel={() => setPhotoEditorOpen(false)} onSet={async (blob) => { await viewerPhoto.set(blob); }} />
      </> : null}
    </main>
  );
}
